import { useCloseDeletedSelection } from "../../sites/components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { SystemAdminService } from "../../services/SystemAdminService";
import { FormControlElement, ILayoutTemplate } from "../../models/Interfaces";
import Loading from "../../sites/components/common/Loading";
import { Core } from "@form-validation/bundle/full";
import { CellProps, Column } from "react-table";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEdit, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useConfirm } from "../../config/ConfirmProvider";
import FilteredTable from "../../sites/components/common/FilteredTable";
import AppModal from "../../sites/components/common/AppModal";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { createFormValidation, isFormValid } from "../../helpers/formValidationHelper";
import styles from "./componentAdmin.module.scss";

const LayoutTemplates = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [templates, setTemplates] = useState<Array<ILayoutTemplate>>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ILayoutTemplate | null>(null);
  const [showDetail, setShowDetail] = useState<boolean>(false);

  const sysAdmSvcRef = useRef<SystemAdminService | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const confirmation = useConfirm();

  const columns: Column<ILayoutTemplate>[] = useMemo(() => [
    {
      Header: "Template Name",
      accessor: "TemplateName"
    },
    {
      Header: "Description",
      accessor: "Description"
    },
    {
      Header: "Column Based",
      Cell: ({ row }: CellProps<ILayoutTemplate>) => {
        if (row.original.ColumnBased) {
          return (<FontAwesomeIcon icon={faCheck} className="text-success" size="2x" style={{ cursor: "pointer" }} />)
        } else {
          return (<FontAwesomeIcon icon={faTimes} size="2x" className="text-success" style={{ cursor: "pointer" }} />)
        }
      }
    },
    {
      Header: "Enabled",
      Cell: ({ row }: CellProps<ILayoutTemplate>) => {
        if (row.original.Enabled) {
          return (<FontAwesomeIcon icon={faCheck} className="text-success" size="2x" style={{ cursor: "pointer" }} />)
        } else {
          return (<FontAwesomeIcon icon={faTimes} size="2x" className="text-danger" style={{ cursor: "pointer" }} />)
        }
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<ILayoutTemplate>) => (
        <div>
          <Button variant="outline-primary" onClick={() => editTemplate(row.original)} className="btn-nomin me-2" title="Edit layout template">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="outline-danger" onClick={() => deleteTemplate(row.original)} className="btn-nomin" title="Delete layout template">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [selectedTemplate, templates]);

  const getTemplates = async (): Promise<void> => {
    if (sysAdmSvcRef.current) {
      const res = await sysAdmSvcRef.current.getLayoutTemplates();
      setTemplates(res);
    }
  };

  const editTemplate = (template: ILayoutTemplate): void => {
    if (template) {
      setSelectedTemplate(template);
      setShowDetail(true);
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedTemplate, (item) => item.Id, () => closeDetail());

  const deleteTemplate = async (template: ILayoutTemplate): Promise<void> => {
    if (template && sysAdmSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation(`Delete template <b>${template.TemplateName}</b> from layout templates?<br /><b>This action cannot be undone.</b>`, "Delete Layout Template");

      if (confirmed) {
        const delres = await sysAdmSvcRef.current.deleteLayoutTemplate(template.Id);

        if (delres) {
          setTemplates(prev => prev.filter(pt => pt.Id !== template.Id));
          closeDeletedSelection(template.Id);
        }
      }
    }
  };

  const addTemplate = (): void => {
    if (sysAdmSvcRef.current) {
      setSelectedTemplate(sysAdmSvcRef.current.newTemplate());
      setShowDetail(true);
    }
  };

  const saveTemplate = async (): Promise<void> => {
    if (selectedTemplate && fvRef.current && sysAdmSvcRef.current) {
      const validres = await isFormValid(fvRef.current);
      if (validres) {
        const saveres = await sysAdmSvcRef.current.saveLayoutTemplate(selectedTemplate);

        if (saveres) {
          const existing = templates.find(pt => pt.Id === saveres.Id);
          if (existing) {
            setTemplates(prev => prev.map(pt => pt.Id === saveres.Id ? saveres : pt));
          } else {
            setTemplates(prev => [
              ...prev,
              saveres
            ]);
          }
          closeDetail();
        }
      }
    }
  };

  const closeDetail = (): void => {
    setSelectedTemplate(null);
    setShowDetail(false);
  };

  const handleInputChange = (event: React.ChangeEvent<FormControlElement>): void => {
    const { name, value } = event.target;

    if (selectedTemplate && name) {
      setSelectedTemplate(prev => ({
        ...prev as ILayoutTemplate,
        [name]: value
      }));
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = event.target;

    if (selectedTemplate && name) {
      setSelectedTemplate(prev => ({
        ...prev as ILayoutTemplate,
        [name]: checked
      }));
    }
  };

  const createValidator = (): void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        TemplateName: {
          validators: {
            notEmpty: { message: "template name is required" }
          }
        },
        Description: {
          validators: {
            notEmpty: { message: "template description is required" }
          }
        }
      });
    }
  };

  useEffect(() => {
    if (showDetail) {
      createValidator();
    } else {
      fvRef.current = null;
    }
  }, [showDetail]);

  useEffect(() => {
    if (!sysAdmSvcRef.current) {
      sysAdmSvcRef.current = new SystemAdminService();
    }

    const getData = async () => {
      try {
        await Promise.all([
          getTemplates()
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    getData();

  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading layout templates..." SpinSize="5x" />
      )}

      {!loading && (
        <>
          <section className={`app-list-hero ${styles.systemSettingsHero}`}>
            <div>
              <p className="app-list-hero__eyebrow">System Administration</p>
              <h1 className="app-list-hero__title">Layout Templates</h1>
              <p className="app-list-hero__summary">Maintain the reusable layout templates available to designers and editors across the CMS.</p>
            </div>
            <Button variant="primary" className={styles.primaryButton} onClick={addTemplate}>
              <FontAwesomeIcon icon={faPlus} />
              <span>Add a layout template</span>
            </Button>
          </section>
          <div className="app-list-section">
            <div className="app-list-section__body">
              <FilteredTable<ILayoutTemplate>
                columns={columns}
                data={templates}
                noDataMessage="No layout templates found, click the button to add"
              />
            </div>
          </div>
        </>
      )}

      {showDetail && selectedTemplate && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Layout Template"
          onDataAction={() => saveTemplate()}>
          <Form id="LayoutTemplateForm" ref={formRef}>
            <Row>
              <Col md={12} className="mt-3 fv-row">
                <FormLabel htmlFor="TemplateName">Template Name</FormLabel>
                <FormControl
                  type="text"
                  value={selectedTemplate.TemplateName}
                  onChange={handleInputChange}
                  name="TemplateName"
                  id="TemplateName"
                  placeholder="set template name (must match the layout template)"
                  title="set template name"
                />
                <div className="fv-validation-message"></div>
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3 fv-row">
                <FormLabel htmlFor="Description">Description</FormLabel>
                <FormControl
                  type="text"
                  value={selectedTemplate.Description}
                  onChange={handleInputChange}
                  name="Description"
                  id="Description"
                  placeholder="set template description"
                  title="set template description"
                />
                <div className="fv-validation-message"></div>
              </Col>
            </Row>
            <Row>
              <Col md={12} lg={6} className="mt-3">
                <FormLabel htmlFor="ColumnBased">Column Based</FormLabel>
                <FormCheckInput
                  className="form-check"
                  checked={selectedTemplate.ColumnBased}
                  onChange={handleCheckChange}
                  name="ColumnBased"
                  id="ColumnBased"
                  title="template is based on bootstrap columns"
                />
              </Col>
              <Col md={12} lg={6} className="mt-3">
                <FormLabel htmlFor="Enabled">Enabled</FormLabel>
                <FormCheckInput
                  className="form-check"
                  checked={selectedTemplate.Enabled}
                  onChange={handleCheckChange}
                  name="Enabled"
                  id="Enabled"
                  title="template available for use"
                />
              </Col>
            </Row>
          </Form>
        </AppModal>
      )}
    </>
  );
}

export default LayoutTemplates;
